<script setup>
import { ref, shallowRef } from 'vue';
import UiFormDialog from '@/components/ui/UiFormDialog.vue';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import { apiRequest } from '@/api/http';
import { useAsyncAction } from '@/composables/useAsyncAction';
import { useListResource } from '@/composables/useListResource';
import { useSessionGuard } from '@/composables/useSessionGuard';

useSessionGuard();

// Senior High tracks (Academic, TVL…), which group the strands.
const headers = [{ title: 'Track', key: 'tracks_name' }];
const { items, loading, error, load } = useListResource(() =>
  apiRequest('get', '/rooms-section/getAlltracks/Data/tracks'),
);
load();

const editing = shallowRef(null); // null = add
const formOpen = shallowRef(false);
const form = ref({ name: '' });
const fields = [{ key: 'name', label: 'Track name', required: true, placeholder: 'e.g. Academic' }];
const { busy, run } = useAsyncAction();

function openForm(track = null) {
  editing.value = track;
  form.value = { name: track?.tracks_name ?? '' };
  formOpen.value = true;
}

async function save() {
  const data = { tracks_name: form.value.name.trim() };
  const ok = await run(
    () =>
      editing.value
        ? apiRequest('patch', `/rooms-section/updateTrack/${editing.value.id}`, data)
        : apiRequest('post', '/rooms-section/addTrack', data),
    { success: editing.value ? 'Track updated.' : 'Track added.', error: 'Could not save the track.' },
  );
  if (ok) {
    formOpen.value = false;
    load();
  }
}
</script>

<template>
  <UiTableCard
    title="Tracks"
    description="Senior High tracks used to group strands."
    :headers="headers"
    :items="items"
    :loading="loading"
    :error="error"
    search-placeholder="Search tracks"
    add-label="Add track"
    empty-text="No tracks yet."
    row-label="tracks_name"
    :row-actions="(track) => [{ title: 'Edit', icon: 'mdi-pencil-outline', onClick: () => openForm(track) }]"
    @add="openForm()"
  >
    <template #[`item.tracks_name`]="{ item }">
      <span class="font-weight-medium">{{ item.tracks_name }}</span>
    </template>
  </UiTableCard>

  <UiFormDialog
    v-model="formOpen"
    :title="editing ? 'Edit track' : 'Add track'"
    description="Tracks group the Senior High strands."
    v-model:values="form"
    :fields="fields"
    :saving="busy"
    :submit-text="editing ? 'Save changes' : 'Add track'"
    @submit="save"
  />
</template>
